import {HttpClient, HttpErrorResponse, HttpParams, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  BaseService,
  CacheManager,
  Callback,
  ConfigurationService,
  DocumentFetchParams,
  DocumentService,
  FragmentCache,
  FragmentMapper,
  UUID
} from '@nh/angular-tses';
import {Subscription} from 'rxjs';
import {DepartureFragment} from '../../fragment/types/departure-fragment';
import {RevisionService} from './revision.service';

/**
 * A wrapper around the document service that provides type coercion and DAS-specific methods
 */
@Injectable({
  providedIn: 'root'
})
export class DepartureService extends BaseService {
  constructor(
    protected _snackbar: MatSnackBar,
    protected _configurationService: ConfigurationService,
    private _httpClient: HttpClient,
    private _documentService: DocumentService<DepartureFragment>,
    private _cacheManager: CacheManager,
    private _revisionService: RevisionService
  ) {
    super(_snackbar, _configurationService);
  }

  public load(
    id: UUID,
    params: DocumentFetchParams,
    addToCache: boolean = true,
    errorSnackbar: string = 'Failed to load the departure'
  ): Promise<DepartureFragment> {
    return this._documentService.load(id, params, addToCache, errorSnackbar);
  }

  public loadUsingDepartureId(
    departureId: number,
    revision: number,
    params: DocumentFetchParams,
    addToCache: boolean = true,
    errorSnackbar: string = 'Failed to load the departure'
  ): Promise<DepartureFragment> {
    const idString: string = departureId ? departureId.toString() : null;

    return this._revisionService.isValidRevision(departureId, revision).then((isValidRevision: boolean) => {
      const httpParams: HttpParams = isValidRevision
        ? this._toHttpParams(params, revision)
        : this._toHttpParams(params);

      const errorHandler = (response: HttpErrorResponse): Promise<never> => {
        this._handleError(response, errorSnackbar, 'fragment-error');
        return Promise.reject(response);
      };

      const cache: FragmentCache = params.validAt
        ? this._cacheManager.getHistoricalCache(params.validAt)
        : this._cacheManager.getLiveCache();

      return this._httpClient
        .get(`${this.config.apiHost}/tree/departure/${idString}`, {params: httpParams, observe: 'response'})
        .toPromise()
        .then((response: HttpResponse<any>) => {
          const departure: DepartureFragment = FragmentMapper.deserialise(response.body) as DepartureFragment;
          departure.updateSectionLabels();

          return (addToCache ? cache.insertTree(departure) : departure) as DepartureFragment;
        }, errorHandler)
        .catch(errorHandler);
    });
  }

  private _toHttpParams(params: DocumentFetchParams, revision?: number): HttpParams {
    let httpParams: HttpParams = new HttpParams();
    if (params.validAt !== undefined) {
      httpParams = httpParams.append('validAt', params.validAt.toString());
    }
    if (params.projection !== undefined) {
      httpParams = httpParams.append('projection', params.projection.toString());
    }
    if (params.setLocation !== undefined) {
      httpParams = httpParams.append('setLocation', params.setLocation.toString());
    }
    if (revision != null && revision >= 0) {
      httpParams = httpParams.append('revision', revision.toString());
    }
    return httpParams;
  }

  public create(): Promise<number> {
    return this._httpClient
      .post<number>(`${this.config.apiHost}/departure`, null)
      .toPromise()
      .catch(err => {
        this._handleError(err, 'Failed to create a departure.  Please contact the DAS team');
        return Promise.reject(err);
      });
  }

  public update(departure: DepartureFragment): Promise<number> {
    return this._documentService.update(departure);
  }

  public onCreate(callback: Callback<DepartureFragment>): Subscription {
    return this._documentService.onCreate(callback);
  }

  public onUpdate(callback: Callback<DepartureFragment>): Subscription {
    return this._documentService.onUpdate(callback);
  }

  public onDelete(callback: Callback<DepartureFragment>): Subscription {
    return this._documentService.onDelete(callback);
  }

  public onSelection(callback: Callback<DepartureFragment>): Subscription {
    return this._documentService.onSelection(callback);
  }

  public setSelected(departure: DepartureFragment): void {
    this._documentService.setSelected(departure);
  }

  public getSelected(): DepartureFragment {
    return this._documentService.getSelected();
  }
}
